import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area, AreaChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import {
  emergencyUnits, activeIncidents, responseTimeline, resourceDeployment, healthData
} from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveResponseSection } from "../components/live/LiveResponseSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  Siren, Shield, Flame, HeartPulse, HardHat, Heart, Clock, MapPin,
  Phone, Radio, Zap, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Users, TrendingUp, Activity, Wifi, WifiOff, Target, TriangleAlert
} from "lucide-react";

// ── inline data ────────────────────────────────────────────────
const INCIDENT_DETAIL = {
  "INC-8821": {
    desc:"Crowd density exceeded 9.2/m² at Ramkund Entry 2. Gates temporarily sealed. Alternate routing via Gate 4 in progress.",
    units:["Police Platoon P-7","Sub-Inspector Gaikwad","Crowd Control Van CC-04"],
    sop:["Seal Entry 2 immediately","Redirect crowd to Gate 4 via PA announcement","Deploy stanchion barriers at Bridge Road","Request CCTV zoom on Gate 2 cluster","Reduce entry to 200 pilgrims/min","Escalate to DCP if crowd density remains >8 after 15 min"],
    confidence:91, aiRec:"AI recommends hard cap at 150/min — crowd velocity indicates surge risk at 15:10",
  },
  "INC-8820": {
    desc:"Water tanker WTK-14 failed to arrive at Panchvati Water Point 8. Heat index 41°C. ORS distribution halted.",
    units:["Municipal Officer MO-3","Tanker Backup TK-22","Volunteer Team V-14"],
    sop:["Dispatch backup tanker TK-22 immediately","Alert Zone B health team for heatstroke watch","Enable mobile ORS vans in 500m radius","Inform CM control room if unresolved in 20 min"],
    confidence:87, aiRec:"AI suggests diverting TK-18 from Gangapur Camp (lower risk zone) to Panchvati — saves 11 min",
  },
  "INC-8819": {
    desc:"Ambulance AMB-07 access blocked at Trimbak Road junction. Patient — heatstroke, serious. Road blocked by unauthorised vehicles.",
    units:["Inspector Shinde (Trimbak)","Ambulance AMB-07","Tow truck TW-02"],
    sop:["Request immediate police escort for AMB-07","Dispatch tow truck TW-02 to clear obstruction","Stabilise patient at Camp Beta until AMB-07 arrives","Escalate to DCP if not cleared in 5 min","Log NCB registration of blocking vehicles"],
    confidence:94, aiRec:"AI predicts 4 more ambulance corridor conflicts in next 2 hrs — recommend dynamic ambulance lane designation",
  },
  "INC-8818": {
    desc:"Nashik Road Camp 3 at 138% occupancy. Structural load limit near. Overflow pilgrims sleeping on approach road.",
    units:["Revenue Officer RO-4","NDRF Platoon N-2","Volunteer Team V-22"],
    sop:["Halt new admissions to Camp 3","Open overflow tent zone OFZ-B (capacity 2,400)","Transport excess occupants via bus BR-09","Conduct headcount and register displaced pilgrims"],
    confidence:84, aiRec:"AI projects Camp 3 structural stress stabilises if 800 pilgrims moved to Gangapur Camp by 16:00",
  },
  "INC-8817": {
    desc:"Missing child ~7yrs, Boy, yellow kurta, last seen Panchvati Sector 7 at 14:10. Parents registered at Camp Beta.",
    units:["Constable Patil","Missing Person Team MP-2","WhatsApp Broadcast sent to 22,000"],
    sop:["Post description at all 6 zone PA systems","Broadcast on WhatsApp, SMS, IVR","Deploy constable at Panchvati entry/exit","Check CCTV at Gate 6 area","Notify Camp Beta parents with updates every 15 min"],
    confidence:79, aiRec:"AI CCTV scan matched: child possibly spotted Gate 5 at 14:28 — request manual confirmation",
  },
  "INC-8816": {
    desc:"Heatstroke cluster at Godavari Zone D — 14 cases in 45 min window. Medical team dispatched, ORS deployed.",
    units:["Medical Inspector MI-2","Camp Gamma Team","Ambulance AMB-03"],
    sop:["Transport critical cases to Civil Hospital","Set up shaded rest zone in Zone D","Increase water distribution in Zone D to 5-min intervals","Surveillance for additional cases"],
    confidence:94, aiRec:"AI heat risk model shows Zone D will cool by 16:30 — maintain medical presence until then",
  },
};

const AMBULANCE_FLEET = [
  { id:"AMB-01", zone:"Ramkund Ghat",    status:"On Scene",  crew:"UNIT-H03 + Para",  eta:"—",    type:"ALS" },
  { id:"AMB-02", zone:"Panchvati",        status:"En Route",  crew:"Para Team B",        eta:"4 min",type:"BLS" },
  { id:"AMB-03", zone:"Godavari Zone D",  status:"On Scene",  crew:"UNIT-H04 + Para",  eta:"—",    type:"ALS" },
  { id:"AMB-04", zone:"Trimbakeshwar",    status:"Available", crew:"Para Team D",        eta:"—",    type:"BLS" },
  { id:"AMB-05", zone:"Nashik Road",      status:"Available", crew:"Para Team E",        eta:"—",    type:"BLS" },
  { id:"AMB-06", zone:"Kalaram Temple",   status:"Available", crew:"Para Team F",        eta:"—",    type:"BLS" },
  { id:"AMB-07", zone:"Camp Beta / Blocked",status:"Blocked", crew:"Para + Dr. Kulk.",  eta:"—",    type:"ALS" },
  { id:"AMB-08", zone:"Gangapur Camp",    status:"Available", crew:"Para Team H",        eta:"—",    type:"BLS" },
];

const FIRE_STATIONS = [
  { station:"Nashik Central FS",  engines:4, pumps:8,  personnel:42, status:"Ready",     coveredZones:["Ramkund","Panchvati"] },
  { station:"Trimbakeshwar FS",   engines:2, pumps:4,  personnel:22, status:"1 Deployed",coveredZones:["Trimbakeshwar"] },
  { station:"Gangapur FS",        engines:2, pumps:4,  personnel:18, status:"Ready",     coveredZones:["Gangapur","Nashik Rd"] },
];

const NDRF_PLATOONS = [
  { id:"NDRF-P1", name:"Platoon 1 — Ramkund QRF", strength:24, readiness:98, status:"Standby", equip:"Flood rescue + stretchers" },
  { id:"NDRF-P2", name:"Platoon 2 — Camp Shelter", strength:24, readiness:94, status:"Active",  equip:"Shelter + evacuation kits" },
  { id:"NDRF-P3", name:"NDRF-HQ Reserve",           strength:18, readiness:100,status:"Reserve", equip:"Full disaster response kit" },
];

const COMMS_STATUS = [
  { channel:"TETRA Radio (P25)",  coverage:98, status:"Operational",  zones:6, backup:"Satellite" },
  { channel:"4G / LTE Network",  coverage:91, status:"Degraded E2",  zones:4, backup:"TETRA" },
  { channel:"Satellite Link",    coverage:100,status:"Operational",  zones:6, backup:"N/A" },
  { channel:"PA System — Zones", coverage:94, status:"Operational",  zones:5, backup:"Hand-held" },
  { channel:"Field Walkie-Talkie",coverage:100,status:"Operational", zones:6, backup:"TETRA" },
];

const SOS_TRIGGERS = [
  { zone:"Ramkund Ghat",    sos:14, responded:14, pending:0, type:"Crowd/Medical" },
  { zone:"Panchvati",       sos:8,  responded:8,  pending:0, type:"Medical"       },
  { zone:"Trimbakeshwar",   sos:5,  responded:4,  pending:1, type:"Medical"       },
  { zone:"Nashik Road Stn", sos:3,  responded:3,  pending:0, type:"Lost Person"   },
  { zone:"Kalaram Temple",  sos:2,  responded:2,  pending:0, type:"Medical"       },
  { zone:"Gangapur Camp",   sos:1,  responded:1,  pending:0, type:"Structural"    },
];

const MUTUAL_AID = [
  { agency:"Pune NDRF Battalion",    eta:"45 min",  strength:80,  type:"NDRF",     status:"On Standby"  },
  { agency:"Aurangabad Ambulance Hub",eta:"55 min", strength:12,  type:"Medical",  status:"Alerted"     },
  { agency:"MH State Disaster Auth.", eta:"30 min", strength:40,  type:"Relief",   status:"On Standby"  },
  { agency:"IAF Sarpotdar (Heli)",    eta:"22 min", strength:2,   type:"Air Med",  status:"On Standby"  },
  { agency:"Army 1 EME Centre",       eta:"90 min", strength:120, type:"Military", status:"Contingency" },
];

const CONTROL_LOG = [
  { time:"14:33", actor:"AI System",          action:"Crowd surge alert triggered — Ramkund Entry 2 density 9.2/m²",     type:"alert"  },
  { time:"14:31", actor:"Sub-Inspector Gaikwad",action:"Gate 2 sealed, crowd diverting to Gate 4",                       type:"action" },
  { time:"14:29", actor:"Inspector Shinde",   action:"Requested police escort for AMB-07 on Trimbak Road",              type:"action" },
  { time:"14:27", actor:"UNIT-H05",        action:"Patient stabilised at Camp Beta — awaiting ambulance",            type:"medical"},
  { time:"14:25", actor:"EOC Controller",      action:"NDRF Platoon 2 activated for Camp 3 shelter overflow",            type:"action" },
  { time:"14:22", actor:"AI System",           action:"Backup tanker TK-22 auto-dispatched to Panchvati Water Point 8", type:"ai"     },
  { time:"14:18", actor:"UNIT-P16",        action:"Missing child INC-8817 registered — broadcast activated",        type:"alert"  },
  { time:"14:15", actor:"Revenue Officer RO-4",action:"Camp 3 occupancy at 138% — escalated to District Collector",     type:"escalate"},
  { time:"14:12", actor:"AI System",           action:"CCTV match: Missing child possibly sighted at Gate 5",           type:"ai"     },
  { time:"14:08", actor:"UNIT-P22",          action:"Heatstroke cluster Zone D — 14 cases — medical team dispatched", type:"medical"},
];

const ESCALATION_CHAIN = [
  { level:1, title:"Zone Officer / PSI",    trigger:"SLA < 50%",   contact:"Field radio + WhatsApp",   time:"0–5 min"  },
  { level:2, title:"Dy. Commissioner / DCP",trigger:"SLA breached",contact:"TETRA + direct call",      time:"5–10 min" },
  { level:3, title:"District Collector",    trigger:"CRITICAL unresolved >15 min",contact:"EOC hotline",time:"10–20 min"},
  { level:4, title:"Chief Minister EOC",    trigger:"Mass casualty / NDRF activation",contact:"State command",time:">20 min"},
];

const HISTORICAL = [
  { event:"Kumbh Nashik 2015",  avgResp:11.2, incidents:384, slaCompliance:74 },
  { event:"Kumbh Nashik 2019",  avgResp:8.4,  incidents:292, slaCompliance:82 },
  { event:"Simhastha 2027 Now", avgResp:6.8,  incidents:186, slaCompliance:91 },
];

const DISPATCH_TIMELINE = [
  { hour:"06:00", ambulance:2, police:4, fire:0, ndrf:0, volunteer:8  },
  { hour:"07:00", ambulance:5, police:8, fire:1, ndrf:0, volunteer:18 },
  { hour:"08:00", ambulance:9, police:14,fire:1, ndrf:1, volunteer:32 },
  { hour:"09:00", ambulance:12,police:18,fire:2, ndrf:1, volunteer:44 },
  { hour:"10:00", ambulance:14,police:22,fire:2, ndrf:1, volunteer:52 },
  { hour:"11:00", ambulance:11,police:18,fire:1, ndrf:0, volunteer:46 },
  { hour:"12:00", ambulance:8, police:14,fire:1, ndrf:0, volunteer:38 },
  { hour:"13:00", ambulance:6, police:10,fire:0, ndrf:0, volunteer:28 },
  { hour:"14:00", ambulance:5, police:8, fire:0, ndrf:0, volunteer:22 },
];

const RESPONSE_TIME = [
  { time:"06:00", ambulance:5.2, police:4.1, fire:8.2, volunteer:18.4 },
  { time:"07:00", ambulance:5.8, police:4.4, fire:8.6, volunteer:20.1 },
  { time:"08:00", ambulance:6.4, police:4.9, fire:9.1, volunteer:22.8 },
  { time:"09:00", ambulance:7.2, police:5.4, fire:9.8, volunteer:24.1 },
  { time:"10:00", ambulance:7.8, police:5.8, fire:10.2,volunteer:25.4 },
  { time:"11:00", ambulance:6.8, police:5.2, fire:9.4, volunteer:23.2 },
  { time:"12:00", ambulance:6.2, police:4.8, fire:8.8, volunteer:21.8 },
  { time:"13:00", ambulance:5.9, police:4.6, fire:8.4, volunteer:20.4 },
  { time:"14:00", ambulance:5.6, police:4.4, fire:8.1, volunteer:19.8 },
];

const SLA_DATA = [
  { category:"Ambulance Dispatch", target:8,   actual:6.2, unit:"min" },
  { category:"Police Response",    target:5,   actual:4.8, unit:"min" },
  { category:"Fire Response",      target:10,  actual:8.4, unit:"min" },
  { category:"Medical Triage",     target:15,  actual:11.2,unit:"min" },
  { category:"NDRF Deployment",    target:30,  actual:22.4,unit:"min" },
  { category:"Volunteer Dispatch", target:20,  actual:24.1,unit:"min" },
];

const UNIT_ICON_MAP = { Police:Shield, Ambulance:HeartPulse, Fire:Flame, NDRF:HardHat, Volunteers:Heart };

// ── helpers ────────────────────────────────────────────────────
// Incident/unit status vocabularies mapped onto the shared status buckets so
// every colored pill/text/border on this page draws from one palette.
const PRIORITY_BUCKET = { CRITICAL: "critical", HIGH: "serious" };
const priClass = p =>
  `text-[8px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${statusClasses(PRIORITY_BUCKET[p] || "warning")}`;

const STATUS_COL_BUCKET = { Escalated: "critical", Responding: "serious", "In Progress": "warning", Active: "warning", Monitoring: "info" };
const statusCol = s => STATUS[STATUS_COL_BUCKET[s] || "good"].text;

const AMBULANCE_BUCKET = { "On Scene": "warning", "En Route": "info", Blocked: "critical" };
const ampColor = s => statusClasses(AMBULANCE_BUCKET[s] || "good");

// "ai" (violet) has no matching bucket in the shared STATUS palette, so it
// keeps a local override instead of being forced into an unrelated bucket.
const LOG_BUCKET = { alert: "critical", action: "info", medical: "good", escalate: "serious" };
const logColor = t => t === "ai" ? "text-violet-500" : STATUS[LOG_BUCKET[t] || "neutral"].text;

const ZONE_STATUS_BUCKET = { critical: "critical", high: "serious", moderate: "warning" };
const zoneBucket = s => ZONE_STATUS_BUCKET[s] || "good";

// Escalation chain severity increases with level index: neutral -> warning -> serious -> critical.
const ESCALATION_LEVEL_BUCKET = ["neutral", "warning", "serious", "critical"];

// Historical benchmark rows go worst (2015) -> best (Simhastha 2027 now).
const HISTORICAL_TREND_COLORS = [statusHex("critical"), statusHex("warning"), statusHex("good")];

// ── component ──────────────────────────────────────────────────
export default function EmergencyResponse() {
  const { respondTo } = useIntelligence({ running: false });
  const t = useTranslate();
  const [expanded, setExpanded]   = useState(null);
  const [unitTab,  setUnitTab]    = useState("fleet");
  const [activeTab,setActiveTab]  = useState("dispatch");
  const [sopDone,  setSopDone]    = useState({});

  const toggleSop = (incId, idx) =>
    setSopDone(prev => ({ ...prev, [`${incId}-${idx}`]: !prev[`${incId}-${idx}`] }));

  // Derived from the shared mockData arrays instead of hardcoded, so these
  // numbers can't silently drift from the data actually rendered below them.
  const todayBench    = HISTORICAL.find(h => h.event === "Simhastha 2027 Now");
  const totalToday     = todayBench.incidents;
  const resolvedToday  = totalToday - activeIncidents.length;
  const ambulanceUnit  = emergencyUnits.find(u => u.type === "Ambulance");
  const policeUnit     = emergencyUnits.find(u => u.type === "Police");

  return (
    <div className="space-y-8">
      <SectionHeader icon="Siren" title="Emergency Response Coordination"
        subtitle="360° EOC · ambulance fleet · fire stations · NDRF platoons · incident SOP · SLA tracker · comms network · SOS triggers · escalation chain · historical benchmarks"
        badge={`${activeIncidents.length} ACTIVE`}
      />


      <LiveResponseSection respondTo={respondTo} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Active Incidents"   value={activeIncidents.length.toString()} icon="Siren"        color="red"    index={0} live />
        <MetricCard title="Total Today"        value={totalToday.toString()}             icon="Activity"     color="amber"  index={1} />
        <MetricCard title="Resolved Today"     value={resolvedToday.toString()}          icon="CheckCircle"  color="green"  index={2} />
        <MetricCard title="Ambulances Live"    value={ambulanceUnit.responding.toString()} icon="HeartPulse" color="red"    index={3} live />
        <MetricCard title="Police Units"       value={policeUnit.responding.toString()}  icon="Shield"       color="blue"   index={4} live />
        <MetricCard title="Avg Response"       value={`${todayBench.avgResp} min`}       icon="Clock"        color="amber"  index={5} />
        <MetricCard title="SLA Compliance"     value={`${todayBench.slaCompliance}%`}    icon="Target"       color="green"  index={6} />
        <MetricCard title="NDRF Standby"       value={`${NDRF_PLATOONS.length} Plt.`}    icon="HardHat"      color="purple" index={7} />
      </div>

      {/* EOC Status Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-yellow-300 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("EOC ACTIVE — THREAT LEVEL: HIGH — 14:33 IST")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Maha Kumbh AI Emergency Operations Centre — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {activeIncidents.length} {t("active incidents")} · {activeIncidents.filter(i => i.priority === "CRITICAL").length} {t("CRITICAL")} · {t("EOC manned 24/7 · Collector-level command authority · All agency radios bridged · NDRF on 22-min standby · AI situational awareness active")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { label:"INC Active", val:activeIncidents.length.toString() },
              { label:"SLA OK",     val:`${todayBench.slaCompliance}%` },
              { label:"AMB Fleet",  val:ambulanceUnit.deployed.toString() },
              { label:"NDRF Ready", val:`${NDRF_PLATOONS.length} Plt` },
            ].map((b, i) => {
              const c = googleColor(i);
              return (
                <div key={b.label} className="rounded-xl px-3 py-2 text-center min-w-[80px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background: c.bgLight, border: `1px solid ${c.border}` }}>
                  <div className="text-xl font-black" style={{ color: c.text }}>{t(b.val)}</div>
                  <div className="text-[9px]" style={{ color: c.text }}>{t(b.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Incident War Room */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Active Incident War Room")}</h3>
        <div className="space-y-2">
          {activeIncidents.map((inc, i) => {
            const isOpen = expanded === inc.id;
            const detail = INCIDENT_DETAIL[inc.id];
            const sopList = detail?.sop || [];
            const doneCount = sopList.filter((_, idx) => sopDone[`${inc.id}-${idx}`]).length;
            return (
              <motion.div key={inc.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className={`rounded-2xl border transition-all ${
                  inc.priority === "CRITICAL" ? `${STATUS.critical.border} ${STATUS.critical.bg}/40 shadow-card`
                                              : `${STATUS.serious.border} ${STATUS.serious.bg}/20`
                } ${isOpen ? "shadow-card-hover" : ""}`}
              >
                <div className="p-4 cursor-pointer flex items-start justify-between gap-3"
                  onClick={() => setExpanded(isOpen ? null : inc.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={priClass(inc.priority)}>{t(inc.priority)}</span>
                      <span className="text-[9px] font-mono text-slate-400">{inc.id}</span>
                      <span className={`text-xs font-black ${statusCol(inc.status)}`}>{t(inc.status)}</span>
                      <span className="badge badge-info text-[9px]">{t(inc.dept)}</span>
                    </div>
                    <p className="text-sm font-black text-slate-900 mb-0.5">{t(inc.title)}</p>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin size={9} />{t(inc.zone)}</span>
                      <span className="flex items-center gap-1"><Clock size={9} />{t("Elapsed:")} {inc.elapsed}</span>
                      <span className="text-blue-700 font-bold">{t("SLA ETA:")} {t(inc.eta)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {inc.priority === "CRITICAL" && (
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500 live-dot" />
                    )}
                    {isOpen
                      ? <ChevronUp size={14} className="text-slate-400" />
                      : <ChevronDown size={14} className="text-slate-400" />}
                  </div>
                </div>

                <AnimatePresence>
                  {isOpen && detail && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-white/60">
                      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/50 rounded-b-2xl">
                        {/* Description + AI Rec */}
                        <div className="md:col-span-2 space-y-3">
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Situation Report")}</div>
                            <p className="text-xs text-slate-700 leading-relaxed">{t(detail.desc)}</p>
                          </div>
                          <div>
                            <div className="text-[9px] font-black text-slate-500 uppercase mb-1">{t("Assigned Units")}</div>
                            <div className="flex flex-wrap gap-1">
                              {detail.units.map(u => (
                                <span key={u} className="badge badge-info text-[9px]">{u}</span>
                              ))}
                            </div>
                          </div>
                          <div className="p-2.5 rounded-xl bg-[#e8f0fe] border border-[#4285F4]/30">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Zap size={10} className="text-[#1a73e8]" />
                              <span className="text-[9px] font-black text-[#1558d6]">{t("AI RECOMMENDATION — Confidence:")} {detail.confidence}%</span>
                            </div>
                            <p className="text-[10px] text-[#1558d6]">{t(detail.aiRec)}</p>
                            <p className="text-[9px] text-[#1a73e8]/70 mt-1">⚠ {t("Human officer approval required before AI recommendation is actioned (DPDP 2023)")}</p>
                          </div>
                        </div>

                        {/* SOP Checklist */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[9px] font-black text-slate-500 uppercase">{t("SOP Checklist")}</div>
                            <span className="text-[9px] font-black text-blue-700">{doneCount}/{sopList.length}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-200 mb-2 overflow-hidden">
                            <motion.div className="h-1.5 rounded-full bg-blue-600"
                              animate={{ width:`${(doneCount/sopList.length)*100}%` }} />
                          </div>
                          <div className="space-y-1.5">
                            {sopList.map((step, idx) => {
                              const done = !!sopDone[`${inc.id}-${idx}`];
                              return (
                                <div key={idx}
                                  className="flex items-start gap-2 cursor-pointer"
                                  onClick={e => { e.stopPropagation(); toggleSop(inc.id, idx); }}
                                >
                                  <div className={`w-3.5 h-3.5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center border ${
                                    done ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                                  }`}>
                                    {done && <CheckCircle size={9} className="text-white" />}
                                  </div>
                                  <span className={`text-[10px] leading-snug ${done ? "line-through text-slate-400" : "text-slate-700"}`}>
                                    {t(step)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Agency Unit Strength */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {emergencyUnits.map((unit, i) => {
          const Icon = UNIT_ICON_MAP[unit.type] || Shield;
          const utilPct = Math.round((unit.responding / unit.deployed) * 100);
          return (
            <motion.div key={unit.type}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
              className="card p-4"
            >
              <div className="w-10 h-10 rounded-xl mb-3 flex items-center justify-center"
                style={{ background: unit.color + "20" }}>
                <Icon size={18} style={{ color: unit.color }} />
              </div>
              <div className="text-2xl font-black text-slate-900">{unit.deployed}</div>
              <div className="text-xs font-black text-slate-700">{t(unit.type)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 mb-3">{t("deployed")}</div>
              <div className="space-y-1 text-[9px]">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("Available")}</span>
                  <span className="font-black text-emerald-600">{unit.available}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("Responding")}</span>
                  <span className="font-black text-red-600">{unit.responding}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("Util. Rate")}</span>
                  <span className="font-black text-blue-600">{utilPct}%</span>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                <motion.div className="h-2 rounded-full" style={{ background: unit.color }}
                  initial={{ width:0 }}
                  animate={{ width:`${Math.round((unit.deployed/(unit.deployed+unit.available))*100)}%` }}
                  transition={{ duration:0.8, delay: i * 0.08 }} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Fleet + NDRF + Fire tabs */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-black text-slate-900">{t("Field Unit Deployment Detail")}</h3>
          <div className="flex gap-1">
            {[{id:"fleet",label:"Ambulance Fleet"},{id:"fire",label:"Fire Stations"},{id:"ndrf",label:"NDRF Platoons"}].map(tab => (
              <button key={tab.id} onClick={() => setUnitTab(tab.id)}
                className={`text-[9px] font-black px-3 py-1.5 rounded-lg transition-colors ${
                  unitTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tab.label)}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {unitTab === "fleet" && (
            <motion.div key="fleet" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {AMBULANCE_FLEET.map((a, i) => (
                <motion.div key={a.id}
                  initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                  className={`p-3 rounded-xl border text-xs ${ampColor(a.status)}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-black font-mono text-sm">{a.id}</span>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full border ${ampColor(a.status)}`}>{t(a.status)}</span>
                  </div>
                  <div className="text-[10px] font-semibold text-slate-700 mb-0.5 truncate">{t(a.zone)}</div>
                  <div className="text-[9px] text-slate-500 mb-1">{a.crew}</div>
                  <div className="flex items-center justify-between text-[9px]">
                    <span className="badge badge-info">{a.type}</span>
                    {a.eta !== "—" && <span className="font-black text-blue-700">{t("ETA:")} {a.eta}</span>}
                  </div>
                </motion.div>
              ))}
              <div className="sm:col-span-2 lg:col-span-4 text-center text-[10px] text-slate-500 pt-1">
                {AMBULANCE_FLEET.filter(a=>a.status==="Available").length} {t("available")} · {AMBULANCE_FLEET.filter(a=>a.status==="On Scene").length} {t("on-scene")} · {AMBULANCE_FLEET.filter(a=>a.status==="En Route").length} {t("en-route")} · {AMBULANCE_FLEET.filter(a=>a.status==="Blocked").length} {t("blocked")}
              </div>
            </motion.div>
          )}
          {unitTab === "fire" && (
            <motion.div key="fire" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-3">
              {FIRE_STATIONS.map((fs, i) => (
                <motion.div key={fs.station}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.08 }}
                  className="p-4 rounded-xl border border-yellow-300 bg-yellow-50/30"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Flame size={14} className="text-amber-600" />
                        <span className="text-sm font-black text-slate-900">{t(fs.station)}</span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${statusClasses(fs.status === "Ready" ? "good" : "serious")}`}>{t(fs.status)}</span>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {fs.coveredZones.map(z => <span key={z} className="badge badge-warning text-[8px]">{t(z)}</span>)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center text-xs">
                      <div><div className="text-xl font-black text-amber-600">{fs.engines}</div><div className="text-[9px] text-slate-500">{t("Engines")}</div></div>
                      <div><div className="text-xl font-black text-blue-600">{fs.pumps}</div><div className="text-[9px] text-slate-500">{t("Pumps")}</div></div>
                      <div><div className="text-xl font-black text-slate-700">{fs.personnel}</div><div className="text-[9px] text-slate-500">{t("Personnel")}</div></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
          {unitTab === "ndrf" && (
            <motion.div key="ndrf" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-3">
              {NDRF_PLATOONS.map((p, i) => (
                <motion.div key={p.id}
                  initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.08 }}
                  className="p-4 rounded-xl border border-blue-200 bg-blue-50/30"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <HardHat size={14} className="text-violet-600" />
                        <span className="text-sm font-black text-slate-900">{t(p.name)}</span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${statusClasses(
                          p.status === "Active" ? "serious" : p.status === "Reserve" ? "info" : "warning"
                        )}`}>{t(p.status)}</span>
                      </div>
                      <div className="text-[10px] text-slate-500">{t(p.equip)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-center text-xs">
                      <div><div className="text-xl font-black text-violet-600">{p.strength}</div><div className="text-[9px] text-slate-500">{t("Strength")}</div></div>
                      <div><div className="text-xl font-black text-emerald-600">{p.readiness}%</div><div className="text-[9px] text-slate-500">{t("Readiness")}</div></div>
                    </div>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-violet-100 overflow-hidden">
                    <motion.div className="h-2 rounded-full bg-violet-500"
                      initial={{ width:0 }} animate={{ width:`${p.readiness}%` }} transition={{ duration:0.8 }} />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Zone Resource Matrix */}
      <ChartCard title="Zone × Resource Deployment Matrix"
        subtitle="Live personnel and unit counts per zone — color coded by zone risk status">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-center text-blue-700">{t("Police")}</th>
                <th className="text-center text-red-600">{t("Ambulance")}</th>
                <th className="text-center text-amber-600">{t("Fire Eng.")}</th>
                <th className="text-center text-emerald-600">{t("Health")}</th>
                <th className="text-center text-violet-600">{t("Volunteers")}</th>
                <th className="text-center">{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {resourceDeployment.map((z, i) => (
                <motion.tr key={z.zone}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className="border-l-2" style={{ borderLeftColor: statusHex(zoneBucket(z.status)) }}
                >
                  <td className="font-bold text-slate-800">
                    <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                  </td>
                  <td className="text-center font-black text-blue-700">{z.police.toLocaleString()}</td>
                  <td className="text-center font-black text-red-600">{z.ambulance}</td>
                  <td className="text-center font-black text-amber-600">{z.fire}</td>
                  <td className="text-center font-black text-emerald-600">{z.health}</td>
                  <td className="text-center font-black text-violet-600">{z.volunteers}</td>
                  <td className="text-center">
                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase ${statusClasses(zoneBucket(z.status))}`}>{t(z.status)}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="SLA Performance Tracker"
          subtitle="Target vs actual response time per emergency category">
          <div className="space-y-4 pt-1">
            {SLA_DATA.map((s, i) => {
              const breach = s.actual > s.target;
              const pct = Math.min(100, Math.round((s.actual / s.target) * 100));
              return (
                <motion.div key={s.category}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{t(s.category)}</span>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400">{t("Target")} {s.target} min</span>
                      <span className={`font-black ${STATUS[breach ? "critical" : "good"].text}`}>
                        {s.actual} min {breach ? `⚠ ${t("BREACH")}` : "✓"}
                      </span>
                    </div>
                  </div>
                  <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className={`h-3 rounded-full ${STATUS[breach ? "critical" : "good"].bar}`}
                      initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.8, delay: i * 0.07 }} />
                    <div className="absolute top-0 bottom-0 w-px bg-blue-600 opacity-60"
                      style={{ left:"100%" }} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Incident Volume vs Resolution" subtitle="Active incidents and resolution rate by hour" live>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={responseTimeline} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Area type="monotone" dataKey="incidents" fill="url(#incGrad)" stroke={chartColor(0)} strokeWidth={2} name={t("Incidents")} />
              <Bar dataKey="resolved"  fill={chartColor(1)} radius={[3,3,0,0]} name={t("Resolved")} />
              <Line type="monotone" dataKey="escalated" stroke={chartColor(2)} strokeWidth={2} dot={{ r:3 }} name={t("Escalated")} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Dispatch + Response Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Emergency Dispatch Timeline — Today"
          subtitle="Hourly dispatch count by unit type">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={DISPATCH_TIMELINE} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="ambulance"  fill={chartColor(0)} radius={[3,3,0,0]} name={t("Ambulance")} stackId="a" />
              <Bar dataKey="police"     fill={chartColor(1)} radius={[3,3,0,0]} name={t("Police")}    stackId="a" />
              <Bar dataKey="fire"       fill={chartColor(2)} radius={[3,3,0,0]} name={t("Fire")}      stackId="a" />
              <Bar dataKey="ndrf"       fill={chartColor(3)} radius={[3,3,0,0]} name={t("NDRF")}      stackId="a" />
              <Bar dataKey="volunteer"  fill={chartColor(4)} radius={[3,3,0,0]} name={t("Volunteers")}stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Response Time Trend — by Unit Type"
          subtitle="Average response time (minutes) — lower is better">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={RESPONSE_TIME} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} unit=" m" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="ambulance"  stroke={chartColor(0)} strokeWidth={2.5} dot={{ r:3 }} name={t("Ambulance (min)")} />
              <Line type="monotone" dataKey="police"     stroke={chartColor(1)} strokeWidth={2.5} dot={{ r:3 }} name={t("Police (min)")} />
              <Line type="monotone" dataKey="fire"       stroke={chartColor(2)} strokeWidth={2.5} dot={{ r:3 }} name={t("Fire (min)")} />
              <Line type="monotone" dataKey="volunteer"  stroke={chartColor(3)} strokeWidth={2}   dot={{ r:3 }} name={t("Volunteer (min)")} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* SOS Triggers + Mutual Aid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="SOS / Panic Trigger Board"
          subtitle="Panic button and SOS call activations by zone — all responded today">
          <div className="space-y-2.5">
            {SOS_TRIGGERS.map((z, i) => (
              <motion.div key={z.zone}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link to="/crowd-intelligence" className="text-xs font-semibold text-slate-800 hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                      <span className="badge badge-warning text-[8px]">{t(z.type)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                    <span className="font-black text-slate-800">{t("Total:")} {z.sos}</span>
                    <span className={`font-black ${STATUS.good.text}`}>{t("Resp:")} {z.responded}</span>
                    {z.pending > 0
                      ? <span className={`font-black ${STATUS.critical.text}`}>{t("Pend:")} {z.pending}</span>
                      : <CheckCircle size={11} className="text-emerald-500" />}
                  </div>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden relative">
                  <motion.div className={`h-2.5 rounded-full ${STATUS.good.bar}`}
                    initial={{ width:0 }} animate={{ width:`${(z.responded/z.sos)*100}%` }}
                    transition={{ duration:0.8, delay: i * 0.07 }} />
                  {z.pending > 0 && (
                    <motion.div className={`absolute top-0 right-0 h-2.5 rounded-r-full ${STATUS.critical.bar}`}
                      initial={{ width:0 }} animate={{ width:`${(z.pending/z.sos)*100}%` }}
                      transition={{ duration:0.8, delay: i * 0.07 }} />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-xl font-black text-slate-900">{SOS_TRIGGERS.reduce((s,z)=>s+z.sos,0)}</div><div className="text-[9px] text-slate-500">{t("Total SOS")}</div></div>
            <div><div className={`text-xl font-black ${STATUS.good.text}`}>{SOS_TRIGGERS.reduce((s,z)=>s+z.responded,0)}</div><div className="text-[9px] text-slate-500">{t("Responded")}</div></div>
            <div><div className={`text-xl font-black ${STATUS.critical.text}`}>{SOS_TRIGGERS.reduce((s,z)=>s+z.pending,0)}</div><div className="text-[9px] text-slate-500">{t("Pending")}</div></div>
          </div>
        </ChartCard>

        <ChartCard title="Mutual Aid & External Reserves"
          subtitle="External agencies pre-positioned for contingency activation">
          <div className="space-y-2">
            {MUTUAL_AID.map((a, i) => (
              <motion.div key={a.agency}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200 hover:shadow-card transition-shadow gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-black text-slate-900 truncate">{t(a.agency)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
                    <span className="badge badge-info">{t(a.type)}</span>
                    <span>{t("Strength:")} {a.strength}</span>
                    <Clock size={8} />
                    <span className="font-bold text-blue-700">{t("ETA")} {a.eta}</span>
                  </div>
                </div>
                <span className={`text-[8px] font-black px-2 py-1 rounded-full flex-shrink-0 ${statusClasses(
                  a.status === "On Standby" ? "warning" : a.status === "Alerted" ? "serious" : "neutral"
                )}`}>{t(a.status)}</span>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Comms + Medical Camps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Communication Network Status"
          subtitle="EOC inter-agency communication channels and redundancy">
          <div className="space-y-3">
            {COMMS_STATUS.map((c, i) => (
              <motion.div key={c.channel}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${
                  c.status === "Operational" ? `${STATUS.good.border} ${STATUS.good.bg}/30` : `${STATUS.warning.border} ${STATUS.warning.bg}/30`
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {c.status === "Operational"
                      ? <Wifi size={12} className="text-emerald-600" />
                      : <WifiOff size={12} className="text-amber-600" />}
                    <span className="text-xs font-black text-slate-800">{t(c.channel)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[9px]">
                    <span className="text-slate-500">{c.zones} {t("zones")}</span>
                    <span className={`font-black ${STATUS[c.status === "Operational" ? "good" : "warning"].text}`}>{t(c.status)}</span>
                    <span className="text-emerald-700 font-bold">{c.coverage}%</span>
                  </div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className={`h-1.5 rounded-full ${STATUS[c.status === "Operational" ? "good" : "warning"].bar}`}
                    initial={{ width:0 }} animate={{ width:`${c.coverage}%` }} transition={{ duration:0.8, delay: i * 0.07 }} />
                </div>
                <div className="text-[9px] text-slate-400 mt-1">{t("Backup:")} {t(c.backup)}</div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Medical Camp Capacity — Live"
          subtitle="Triage camp load vs capacity (patients)">
          <div className="space-y-3">
            {healthData.camps.map((c, i) => {
              const pct = Math.round((c.load / c.capacity) * 100);
              const campBucket = c.status === "over" ? "critical" : c.status === "near" ? "warning" : null;
              return (
                <motion.div key={c.name}
                  initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.08 }}
                  className={`p-3 rounded-xl border ${
                    campBucket ? `${STATUS[campBucket].border} ${STATUS[campBucket].bg}/30` : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5 gap-2">
                    <span className="text-xs font-black text-slate-800">{t(c.name)}</span>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <span className="text-slate-500">{c.load}/{c.capacity} {t("beds")}</span>
                      <span className={`font-black ${STATUS[campBucket || "good"].text}`}>{pct}% {c.status === "over" ? `⚠ ${t("OVER")}` : c.status === "near" ? t("NEAR") : t("OK")}</span>
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className={`h-3 rounded-full ${STATUS[campBucket || "good"].bar}`}
                      initial={{ width:0 }} animate={{ width:`${Math.min(pct, 100)}%` }}
                    transition={{ duration:0.8, delay: i * 0.08 }} />
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-base font-black text-slate-900">{healthData.heatstroke}</div><div className="text-[9px] text-slate-500">{t("Heatstroke")}</div></div>
            <div><div className="text-base font-black text-slate-900">{healthData.dehydration}</div><div className="text-[9px] text-slate-500">{t("Dehydration")}</div></div>
            <div><div className="text-base font-black text-slate-900">{healthData.minorInjury}</div><div className="text-[9px] text-slate-500">{t("Minor Injury")}</div></div>
            <div><div className="text-base font-black text-red-600">{healthData.seriousCase}</div><div className="text-[9px] text-slate-500">{t("Serious")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Escalation Chain + Historical Benchmark */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Escalation Protocol — Incident Command Chain"
          subtitle="Automatic escalation levels triggered by SLA breach or CRITICAL status">
          <div className="relative pl-6">
            <div className="absolute left-2.5 top-0 bottom-0 w-px bg-slate-200" />
            {ESCALATION_CHAIN.map((e, i) => (
              <motion.div key={e.level}
                initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.1 }}
                className="relative mb-4 last:mb-0"
              >
                <div className={`absolute -left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-black ${
                  ESCALATION_LEVEL_BUCKET[i] === "neutral"
                    ? "bg-slate-100 border-slate-400 text-slate-600"
                    : `${STATUS[ESCALATION_LEVEL_BUCKET[i]].bg} ${STATUS[ESCALATION_LEVEL_BUCKET[i]].text} border-current`
                }`}>{e.level}</div>
                <div className={`p-3 rounded-xl border ml-2 ${
                  ESCALATION_LEVEL_BUCKET[i] === "neutral"
                    ? "border-slate-200 bg-slate-50"
                    : `${STATUS[ESCALATION_LEVEL_BUCKET[i]].border} ${STATUS[ESCALATION_LEVEL_BUCKET[i]].bg}/30`
                }`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-xs font-black text-slate-900">{t(e.title)}</div>
                      <div className="text-[9px] text-slate-500 mt-0.5">{t(e.contact)}</div>
                    </div>
                    <div className="text-right text-[9px]">
                      <div className="font-bold text-blue-700">{e.time}</div>
                      <div className="text-slate-400">{t(e.trigger)}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Historical Response Benchmark"
          subtitle="Simhastha 2027 vs Kumbh Nashik 2015 & 2019">
          <div className="space-y-5">
            {[
              { label:"Avg Response Time (min)", key:"avgResp",     better:"lower",  color:HISTORICAL_TREND_COLORS, unit:" min" },
              { label:"Total Incidents",         key:"incidents",   better:"lower",  color:HISTORICAL_TREND_COLORS, unit:"" },
              { label:"SLA Compliance (%)",      key:"slaCompliance",better:"higher",color:HISTORICAL_TREND_COLORS, unit:"%" },
            ].map(m => (
              <div key={m.label}>
                <div className="text-[10px] font-black text-slate-600 uppercase mb-2">{t(m.label)}</div>
                {HISTORICAL.map((h, i) => (
                  <div key={h.event} className="flex items-center gap-3 mb-1.5">
                    <span className="text-[9px] text-slate-600 w-36 flex-shrink-0 truncate">{t(h.event)}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div className="h-2.5 rounded-full" style={{ background: m.color[i] }}
                        initial={{ width:0 }}
                        animate={{ width: m.better === "lower"
                          ? `${Math.round((Math.min(...HISTORICAL.map(x=>x[m.key]))/h[m.key])*90)}%`
                          : `${Math.round((h[m.key]/Math.max(...HISTORICAL.map(x=>x[m.key])))*90)}%` }}
                        transition={{ duration:0.9, delay: i * 0.1 }} />
                    </div>
                    <span className="text-[10px] font-black text-slate-800 w-14 text-right flex-shrink-0">
                      {h[m.key]}{m.unit}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Control Room Log */}
      <ChartCard title="EOC Control Room Activity Log"
        subtitle="Real-time rolling log of officer and AI actions in the Emergency Operations Centre" live>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-0.5">
          {CONTROL_LOG.map((l, i) => (
            <motion.div key={i}
              initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
              className="flex items-start gap-3 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <span className="font-mono text-[9px] text-slate-400 flex-shrink-0 mt-0.5">{l.time}</span>
              <span className={`text-[9px] font-black flex-shrink-0 mt-0.5 ${logColor(l.type)}`}>
                {l.type === "ai" ? "🤖 AI" : l.type === "medical" ? "🏥" : l.type === "escalate" ? "⬆️" : l.type === "alert" ? "🚨" : "👮"}
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-black text-slate-700">{l.actor} — </span>
                <span className="text-[10px] text-slate-600">{t(l.action)}</span>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-[9px] text-violet-600 font-bold mt-3 border-t border-slate-100 pt-2">
          ⚠ {t("AI actions logged for DPDP 2023 audit trail. All AI-triggered field actions require human officer sign-off within 60 seconds.")}
        </p>
      </ChartCard>
    </div>
  );
}





